export const formatByOriginalDecimals = (amount: number): string => {
  let value = amount

  if (!Number.isFinite(value)) value = 0
  let s = String(value).trim()

  if (/e/i.test(s)) {
    let n = Number(s)
    if (!Number.isFinite(n)) n = 0
    s = n
      .toFixed(20)
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.$/, '')
  }

  // Signo
  let sign = ''
  if (s.startsWith('-')) {
    sign = '-'
    s = s.slice(1)
  }

  // Validación básica: dígitos con punto opcional
  if (!/^\d+(\.\d*)?$/.test(s)) s = '0.00'

  const [intPart, decRaw = ''] = s.split('.')
  const decLen = decRaw.length

  // Regla: clamp de decimales entre 2 y 4, pero respetando 3 y 2 exactos
  const keep = Math.min(Math.max(decLen, 2), 4)

  // Truncar y/o rellenar
  const decTruncated = decRaw.slice(0, keep).padEnd(keep, '0')

  return `${sign}${intPart}.${decTruncated}`
}

/**
 * Multiplica dos valores decimales EXACTAMENTE (sin punto flotante binario).
 * - Acepta string o number (recomendado: string para no heredar error previo).
 * - No redondea.
 * - Quita ceros a la derecha innecesarios en la parte decimal.
 * - Devuelve string (para conservar todos los decimales exactos).
 */
export const multiplyExactDecimal = (a: number, b: number): number => {
  if (isNaN(a) || isNaN(b)) return 0
  const norm = (x: string | number) => normalizeDecimal(String(x).trim())

  const A = norm(a)
  const B = norm(b)

  // producto entero exacto de los dígitos
  const prod = (A.int as bigint) * (B.int as bigint)
  const scale = A.scale + B.scale // total de decimales en el resultado

  return formatWithScale(prod, scale)
}

/** Convierte "mantisa (con o sin . y e/E)" a { int: BigInt de los dígitos, scale: decimales } */
function normalizeDecimal(s: string): { int: bigint; scale: number; sign: 1 | -1 } {
  // Signo
  let sign: 1 | -1 = 1
  if (s.startsWith('+')) s = s.slice(1)
  if (s.startsWith('-')) {
    sign = -1
    s = s.slice(1)
  }

  // Separar exponentes si existen (e.g. "1.23e-4")
  let mant = s,
    exp = 0
  const ePos = s.search(/e/i)
  if (ePos !== -1) {
    mant = s.slice(0, ePos)
    exp = parseInt(s.slice(ePos + 1), 10)
    if (Number.isNaN(exp)) throw new Error(`Exponente inválido en "${s}"`)
  }

  // Partes de la mantisa
  if (!/^\d+(\.\d+)?$/.test(mant)) {
    throw new Error(`Formato decimal no soportado: "${mant}"`)
  }
  const [iPart, dPart = ''] = mant.split('.')
  let digits = (iPart + dPart).replace(/^0+(?=\d)/, '') // quitar ceros a la izquierda, dejar al menos un dígito
  if (digits === '') digits = '0'

  // Escala base (decimales de la mantisa)
  let scale = dPart.length

  // Aplicar exponente decimal ("e"): mover el punto
  if (exp !== 0) {
    scale = scale - exp // exp positivo mueve punto a la derecha (reduce escala)
    if (scale < 0) {
      // necesitamos añadir ceros a la derecha de los dígitos
      digits = digits + '0'.repeat(-scale)
      scale = 0
    }
  }

  // Caso cero: normalizar a "0" con escala 0
  if (/^0+$/.test(digits)) {
    return { int: BigInt(0), scale: 0, sign: 1 }
  }

  // Aplicar signo al entero
  const bi = BigInt(digits) * BigInt(sign)
  return { int: bi, scale, sign }
}

/** Inserta punto decimal según 'scale' y quita ceros redundantes de la fracción */
function formatWithScale(n: bigint, scale: number): number {
  const neg = n < 0n ? '-' : ''
  let s = (n < 0n ? -n : n).toString()

  // Asegurar longitud suficiente para insertar punto
  if (scale > 0) {
    if (s.length <= scale) {
      s = '0'.repeat(scale - s.length + 1) + s // +1 para asegurar "0.xxx"
    }
    const idx = s.length - scale
    let whole = s.slice(0, idx)
    let frac = s.slice(idx)

    // Quitar ceros a la derecha de la fracción
    frac = frac.replace(/0+$/, '')
    if (frac.length === 0) {
      return Number(neg + whole) // sin punto si no quedan decimales
    }
    // Quitar ceros a la izquierda del entero (pero dejar al menos "0")
    whole = whole.replace(/^0+(?=\d)/, '')
    if (whole === '') whole = '0'
    return Number(neg + whole + '.' + frac)
  } else {
    // Sin decimales
    return Number(neg + s)
  }
}
